import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';

const SnapshotViewerModal = ({ isOpen, onClose, orderId }) => {
    const [snapshotData, setSnapshotData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModified, setIsModified] = useState(false);
    const [currentStatus, setCurrentStatus] = useState('');

    useEffect(() => {
        if (isOpen && orderId) {
            fetchSnapshot();
        } else {
            setSnapshotData(null);
            setError(null);
        }
    }, [isOpen, orderId]);

    const fetchSnapshot = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/packaging/snapshot/${orderId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error cargando snapshot');
            }

            const data = await response.json();
            if (data.success) {
                setSnapshotData(data.snapshot);
                setIsModified(data.is_modified);
                setCurrentStatus(data.current_status);
            } else {
                setError(data.message || 'No se pudo cargar el snapshot');
            }
        } catch (err) {
            console.error(err);
            setError('Error de conexión al cargar snapshot');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Helper para el estado del ítem
    const getItemStatus = (item) => {
        if (item.current_quantity === 0) return 'deleted'; // Fue borrado de SIIGO
        if (item.is_new) return 'new'; // No existía en el snapshot original (Base)
        if (item.current_quantity !== item.quantity) return 'modified'; // Cantidad difiere de Base
        return 'normal';
    };

    // [NUEVO] Helper para detectar si cambió en este último sync
    const getRecentChangeLabel = (item) => {
        if (!snapshotData?.last_delta) return null;
        const d = snapshotData.last_delta;
        const key = item.product_code || item.name || '';
        const normKey = key.toString().trim().toLowerCase();

        const mod = d.modified?.find(m => m.name.toLowerCase() === item.name.toLowerCase());
        if (mod) {
            return {
                type: mod.diff > 0 ? 'increase' : 'decrease',
                text: `${mod.diff > 0 ? '⬆️' : '⬇️'} ${Math.abs(mod.diff)}`,
                title: `Último sync: ${mod.oldQty} -> ${mod.newQty}`
            };
        }

        const add = d.added?.find(a => a.name.toLowerCase() === item.name.toLowerCase());
        if (add) return { type: 'new_sync', text: '✨ NUEVO', title: 'Agregado en el último sync' };

        const rem = d.removed?.find(r => r.name.toLowerCase() === item.name.toLowerCase());
        if (rem) return { type: 'rem_sync', text: '🗑️ QUITADO', title: 'Eliminado en el último sync' };

        return null;
    };

    const isPackagingStarted = currentStatus === 'en_empaque' || currentStatus === 'empacado' || currentStatus === 'listo_para_entrega' || currentStatus === 'en_reparto' || currentStatus === 'entregado';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" style={{ zIndex: 10000 }}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2">
                        <Icons.Camera className="w-5 h-5 text-blue-600" />
                        Control de Empaque
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-200 focus:outline-none"
                    >
                        <Icons.X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto bg-gray-50">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 text-red-700 p-4 rounded-md text-sm border border-red-200">
                            {error}
                        </div>
                    ) : !snapshotData ? (
                        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                                <Icons.PackageOpen className="w-8 h-8 text-gray-400" />
                            </div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Empaque no iniciado</h4>
                            <p className="text-gray-500 max-w-sm mx-auto">
                                Este pedido aún no ha iniciado el proceso de empaque. El snapshot se generará automáticamente cuando un empacador comience a trabajar en él.
                            </p>

                            {/* Opción de mostrar items actuales como preview podría ir aquí si se quiere */}
                        </div>
                    ) : (
                        <div>
                            {/* Resumen del estado header */}
                            <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border border-gray-100">
                                <div className="flex items-start gap-3">
                                    <div className={`mt-1 p-2 rounded-full ${isModified ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                                        {isModified ? <Icons.AlertTriangle className="w-5 h-5" /> : <Icons.ClipboardCheck className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">
                                            {isModified ? 'Pedido Modificado' : 'Snapshot de Verificación'}
                                        </h4>
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            Iniciado el: <span className="font-medium text-gray-700">{snapshotData.captured_at ? format(new Date(snapshotData.captured_at), 'dd MMM yyyy, HH:mm', { locale: es }) : 'N/A'}</span>
                                        </p>
                                        {isModified && (
                                            <p className="text-xs text-orange-600 mt-1 font-medium bg-orange-50 px-2 py-1 rounded inline-block">
                                                El pedido fue modificado en SIIGO después de iniciar el empaque. Revise las diferencias abajo.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Tabla Comparativa */}
                            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                                        <tr>
                                            <th className="px-4 py-3 text-center w-12">Estado</th>
                                            <th className="px-4 py-3 text-left">Producto</th>
                                            <th className="px-4 py-3 text-center w-24">Original</th>
                                            <th className="px-4 py-3 text-center w-24">Actual</th>
                                            <th className="px-4 py-3 text-center w-24">Sync</th>
                                            <th className="px-4 py-3 text-center w-28 bg-blue-50 text-blue-700">Progreso</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {snapshotData.items.map((item, idx) => {
                                            const status = getItemStatus(item);
                                            // Determinar estilo de fila
                                            let rowClass = "hover:bg-gray-50 transition-colors";
                                            if (status === 'new') rowClass = "bg-green-50 hover:bg-green-100";
                                            if (status === 'deleted') rowClass = "bg-red-50 hover:bg-red-100 opacity-70";
                                            if (status === 'modified') rowClass = "bg-orange-50 hover:bg-orange-100";

                                            const isComplete = item.scanned_count === item.current_quantity && item.current_quantity > 0;
                                            const isExcess = item.scanned_count > item.current_quantity && item.current_quantity > 0;
                                            const recent = getRecentChangeLabel(item);

                                            return (
                                                <tr key={idx} className={rowClass}>
                                                    <td className="px-4 py-3 text-center">
                                                        {status === 'new' && (
                                                            <span title="Nuevo ítem (Agregado post-snapshot)" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                                                                <Icons.Plus className="w-4 h-4" />
                                                            </span>
                                                        )}
                                                        {status === 'deleted' && (
                                                            <span title="Eliminado de SIIGO" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600">
                                                                <Icons.Trash2 className="w-4 h-4" />
                                                            </span>
                                                        )}
                                                        {status === 'modified' && (
                                                            <span title="Cantidad modificada" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-600">
                                                                <Icons.RefreshCw className="w-3 h-3" />
                                                            </span>
                                                        )}
                                                        {status === 'normal' && isComplete && (
                                                            <span title="Verificado 100%" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                                                                <Icons.Check className="w-4 h-4" />
                                                            </span>
                                                        )}
                                                        {status === 'normal' && isExcess && (
                                                            <span title="ERROR: Exceso de empaque" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600 animate-bounce">
                                                                <Icons.AlertCircle className="w-4 h-4" />
                                                            </span>
                                                        )}
                                                        {status === 'normal' && !isComplete && !isExcess && (
                                                            <span title="Pendiente" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-400">
                                                                <Icons.Circle className="w-4 h-4" />
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className={`text-sm font-medium ${status === 'deleted' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                                            {item.name}
                                                        </div>
                                                        <div className="text-xs text-gray-400 font-mono mt-0.5">{item.product_code || item.code}</div>
                                                        {status === 'new' && <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Nuevo</span>}
                                                    </td>
                                                    <td className="px-4 py-3 text-center text-sm text-gray-500">
                                                        {item.quantity}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className={`text-sm font-bold ${item.current_quantity !== item.quantity ? 'text-orange-600' : 'text-gray-700'}`}>
                                                            {item.current_quantity}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {recent && (
                                                            <span
                                                                title={recent.title}
                                                                className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${recent.type === 'increase' ? 'bg-green-100 text-green-700' :
                                                                    recent.type === 'decrease' ? 'bg-red-100 text-red-700' :
                                                                        'bg-blue-100 text-blue-700'
                                                                    }`}
                                                            >
                                                                {recent.text}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center bg-blue-50/30">
                                                        {item.current_quantity > 0 ? (
                                                            <div className="flex flex-col items-center">
                                                                <span className={`text-sm font-bold ${isComplete ? 'text-green-600' : isExcess ? 'text-red-700' : 'text-blue-600'}`}>
                                                                    {item.scanned_count} / {item.current_quantity}
                                                                    {isExcess && (
                                                                        <span className="block text-[10px] bg-red-100 text-red-800 px-1 rounded mt-0.5 font-extrabold animate-pulse">
                                                                            ({Math.round((item.scanned_count / item.current_quantity) * 100)}% - RETIRAR)
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                {/* Mini barra de progreso */}
                                                                <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                                                    <div
                                                                        className={`h-full rounded-full ${isComplete ? 'bg-green-500' : isExcess ? 'bg-red-500' : 'bg-blue-500'}`}
                                                                        style={{ width: `${Math.min(100, (item.scanned_count / item.current_quantity) * 100)}%` }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500 border-t pt-3">
                                <div className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span> Item Nuevo
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span> Modificado
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-red-500"></span> Eliminado
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 rounded-b-lg flex justify-end">
                    <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm transition-all hover:shadow-md"
                        onClick={onClose}
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SnapshotViewerModal;
